import type {
  BillingPortalLink,
  AccountBilling,
  CancelBillingResult,
  ReactivateBillingResult,
  SeatQuantityChangeResult,
} from "@/domain/model/AccountBilling";
import type { AccountManagementService } from "@/domain/port/AccountManagementService";
import type { HttpClient } from "@/infrastructure/HttpClient";
import type {
  CancelBillingRequestDto,
  CancelBillingResponseDto,
  DecreaseSeatsRequestDto,
  DecreaseSeatsResponseDto,
  GetAccountBillingResponseDto,
  GetBillingPortalResponseDto,
  IncreaseSeatsRequestDto,
  IncreaseSeatsResponseDto,
  ReactivateBillingResponseDto,
} from "./dto/AccountBillingDto";
import {
  mapBillingPortalResponseDtoToModel,
  mapCancelBillingResponseDtoToModel,
  mapDecreaseSeatsResponseDtoToModel,
  mapGetAccountBillingResponseDtoToModel,
  mapIncreaseSeatsResponseDtoToModel,
  mapReactivateBillingResponseDtoToModel,
} from "./mappers/AccountBillingMapper";
import type {
  AccountSeatOverview,
  ChangeAccountUserRoleCommand,
  ChangeAccountUserRoleResult,
  GrantPersonAccessCommand,
  GrantPersonAccessResult,
  InviteAccountUserCommand,
  InviteAccountUserResult,
  RemovePersonSeatCommand,
  RemovePersonSeatResult,
  RestorePersonSeatCommand,
  RestorePersonSeatResult,
} from "@/domain/model/AccountSeatOverview";
import type {
  ChangeAccountUserRoleResultDto,
  GrantPersonAccessRequestDto,
  InviteResponseDto,
  RemovePersonFromSeatRequestDto,
  RemovePersonFromSeatResponseDto,
  ResendInvitationResponseDto,
  RestorePersonSeatRequestDto,
  RestorePersonSeatResponseDto,
  SeatOverviewResponseDto,
} from "./dto/AccountSeatDto";
import {
  mapChangeAccountUserRoleCommandToDto,
  mapChangeAccountUserRoleResultDtoToModel,
  mapGrantPersonAccessCommandToDto,
  mapGrantPersonAccessResponseDtoToResult,
  mapInviteAccountUserCommandToDto,
  mapInviteResponseDtoToResult,
  mapRemovePersonSeatResponseDtoToResult,
  mapResendInvitationResponseDtoToResult,
  mapRestorePersonSeatResponseDtoToResult,
  mapSeatOverviewResponseDtoToModel,
} from "./mappers/AccountSeatMapper";
import type { ResendAccountInvitationCommand, ResendAccountInvitationResult, RevokeInvitationCommand } from "@/domain/model/Invitation";

export class AccountManagementServiceImpl implements AccountManagementService {
  constructor(private readonly http: HttpClient) {}

  async getBilling(signal?: AbortSignal): Promise<AccountBilling> {
    const dto = await this.http.request<GetAccountBillingResponseDto>(
      "/api/account/billing",
      { method: "GET", signal },
    );

    return mapGetAccountBillingResponseDtoToModel(dto);
  }

  async increaseSeats(
    targetSeatQuantity: number,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<SeatQuantityChangeResult> {
    const body: IncreaseSeatsRequestDto = { targetSeatQuantity };

    const dto = await this.http.request<IncreaseSeatsResponseDto>(
      "/api/account/billing/seats/increase",
      {
        method: "POST",
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify(body),
        signal,
      },
    );

    return mapIncreaseSeatsResponseDtoToModel(dto);
  }

  async decreaseSeats(
    targetSeatQuantity: number,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<SeatQuantityChangeResult> {
    const body: DecreaseSeatsRequestDto = { targetSeatQuantity };

    const dto = await this.http.request<DecreaseSeatsResponseDto>(
      "/api/account/billing/seats/decrease",
      {
        method: "POST",
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify(body),
        signal,
      },
    );

    return mapDecreaseSeatsResponseDtoToModel(dto);
  }

  async cancelBilling(
    cancelAtPeriodEnd: boolean,
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<CancelBillingResult> {
    const body: CancelBillingRequestDto = { cancelAtPeriodEnd };

    const dto = await this.http.request<CancelBillingResponseDto>(
      "/api/account/billing/cancel",
      {
        method: "POST",
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify(body),
        signal,
      },
    );

    return mapCancelBillingResponseDtoToModel(dto);
  }

  async reactivateBilling(
    idempotencyKey: string,
    signal?: AbortSignal,
  ): Promise<ReactivateBillingResult> {
    const dto = await this.http.request<ReactivateBillingResponseDto>(
      "/api/account/billing/reactivate",
      {
        method: "POST",
        headers: {
          "Idempotency-Key": idempotencyKey,
        },
        signal,
      },
    );

    return mapReactivateBillingResponseDtoToModel(dto);
  }

  async getBillingPortalLink(
    returnUrl: string,
    signal?: AbortSignal,
  ): Promise<BillingPortalLink> {
    const query = new URLSearchParams({ returnUrl }).toString();

    const dto = await this.http.request<GetBillingPortalResponseDto>(
      `/api/account/billing/portal?${query}`,
      { method: "GET", signal },
    );

    return mapBillingPortalResponseDtoToModel(dto);
  }

  async getAccountSeats(signal?: AbortSignal): Promise<AccountSeatOverview> {
    const dto = await this.http.request<SeatOverviewResponseDto>(
      "/api/account/seats",
      { method: "GET", signal },
    );

    return mapSeatOverviewResponseDtoToModel(dto);
  }

  async inviteAccountUser(
    command: InviteAccountUserCommand,
    signal?: AbortSignal,
  ): Promise<InviteAccountUserResult> {
    const dto = await this.http.request<InviteResponseDto>(
      `/api/accounts/invitations`,
      {
        method: "POST",
        body: JSON.stringify(mapInviteAccountUserCommandToDto(command)),
        signal,
      },
    );

    return mapInviteResponseDtoToResult(dto);
  }

  async grantPersonAccess(
    command: GrantPersonAccessCommand,
    signal?: AbortSignal,
  ): Promise<GrantPersonAccessResult> {
    const body: GrantPersonAccessRequestDto =
      mapGrantPersonAccessCommandToDto(command);

    const dto = await this.http.request<InviteResponseDto>(
      `/api/account/persons/${command.personId}/access`,
      {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      },
    );

    return mapGrantPersonAccessResponseDtoToResult(dto);
  }

  async removePersonSeat(
    command: RemovePersonSeatCommand,
    signal?: AbortSignal,
  ): Promise<RemovePersonSeatResult> {
    const body: RemovePersonFromSeatRequestDto = {
      personId: command.personId,
    };

    const dto = await this.http.request<RemovePersonFromSeatResponseDto>(
      "/api/account/seats/remove",
      {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      },
    );

    return mapRemovePersonSeatResponseDtoToResult(dto);
  }

  async restorePersonSeat(
    command: RestorePersonSeatCommand,
    signal?: AbortSignal,
  ): Promise<RestorePersonSeatResult> {
    const body: RestorePersonSeatRequestDto = {
      personId: command.personId,
    };

    const dto = await this.http.request<RestorePersonSeatResponseDto>(
      "/api/account/seats/restore",
      {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      },
    );

    return mapRestorePersonSeatResponseDtoToResult(dto);
  }

  async resendAccountInvitation(
    command: ResendAccountInvitationCommand,
    signal?: AbortSignal,
  ): Promise<ResendAccountInvitationResult> {
    const dto = await this.http.request<ResendInvitationResponseDto>(
      `/api/accounts/invitations/${command.invitationId}/resend`,
      {
        method: "POST",
        signal,
      },
    );

    return mapResendInvitationResponseDtoToResult(dto);
  }

  async revokeInvitation(
    command: RevokeInvitationCommand,
    signal?: AbortSignal
  ): Promise<void> {
    await this.http.request(
      `/api/accounts/invitations/${command.invitationId}/revoke`,
      {
        method: "POST",
        signal,
      },
    );
  }

  async changeAccountUserRole(
    cmd: ChangeAccountUserRoleCommand,
    signal?: AbortSignal,
  ): Promise<ChangeAccountUserRoleResult> {
    const body = mapChangeAccountUserRoleCommandToDto(cmd);

    const dto = await this.http.request<ChangeAccountUserRoleResultDto>(
      "/api/accounts/users/role",
      {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      },
    );

    return mapChangeAccountUserRoleResultDtoToModel(dto);
  }
}
